import {
  jsonResponseParser,
  ResponseParser,
  failureOnStatusCodeFactory,
  textStreamingResponseParser,
  emptyResponseParser,
  serverSideEventsStreamingResponseParser,
  ServerSentEvent
} from './ResponseParser';
import { HttpMethod } from './constants';

export type HttpRequest<R> = {
  path: string;
  request: RequestInit;
  responseParser: ResponseParser<R>;
  authMiddleware: (res: Response) => Promise<Response>;
};

export const doHttpRequest = async <T>({
  path,
  request,
  responseParser,
  authMiddleware
}: HttpRequest<T>) => {
  return fetch(path, request).then(authMiddleware).then(responseParser);
};

export const createServerSentEventsSource = (path: string): EventSource => {
  const eventSource = new EventSource(path, {
    withCredentials: true
  });

  return eventSource;
};

type QueryParams = Record<string, string | number | boolean>;

export class PathBuilder {
  private readonly path: string;
  // private productId: string = '';
  private queryParams: QueryParams = {};

  private constructor(path: string) {
    this.path = path;
  }

  static aPath(path: string) {
    return new PathBuilder(path);
  }

  withQueryParams(params: QueryParams) {
    this.queryParams = { ...this.queryParams, ...params };
    return this;
  }

  build() {
    return `${process.env.VUE_APP_API_URL}${this.path}${this.getQueryString(this.queryParams)}`;
  }

  private getQueryString(queryParams: QueryParams): string {
    const queryString = Object.entries(queryParams)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      )
      .join('&');
    return queryString ? `?${queryString}` : '';
  }
}

export class RequestBuilder<T> {
  private readonly request: RequestInit = {};
  private path: string = '';
  private queryParams: QueryParams = {};
  private parser?: ResponseParser<T>;
  // private productId: string = '';

  private constructor(method: string) {
    this.request.method = method;
    this.request.credentials = 'include';
  }

  static aGetRequest<T>() {
    return new RequestBuilder<T>(HttpMethod.GET);
  }

  static aPostRequest<T>() {
    return new RequestBuilder<T>(HttpMethod.POST);
  }

  static aPutRequest<T, R>(body: R) {
    return new RequestBuilder<T>(HttpMethod.PUT).withJsonBody<R>(body);
  }

  static aDeleteRequest<T>() {
    return new RequestBuilder<T>(HttpMethod.DELETE);
  }

  withHeader(key: string, value: string) {
    this.request.headers = {
      ...(this.request.headers || {}),
      [key]: value
    };
    return this;
  }

  withJsonHeaders() {
    this.withHeader('Accept', 'application/json');
    this.withHeader('Content-Type', 'application/json');
    return this;
  }

  withPath(path: string) {
    this.path = `${path}`;
    return this;
  }

  withJsonBody<T>(body: T) {
    this.request.body = JSON.stringify(body);
    return this;
  }

  withFileBody<T extends File>(body: T) {
    const formData = new FormData();
    formData.append('file', body, body.name);
    this.request.body = formData;
    return this;
  }

  withQueryParams(params: QueryParams) {
    this.queryParams = { ...this.queryParams, ...params };
    return this;
  }

  withJsonResponse() {
    this.parser = jsonResponseParser;
    return this;
  }

  withTextStreamingResponse() {
    this.parser = textStreamingResponseParser as ResponseParser<T>;
    return this;
  }

  withServerSentEventsStreamingResponse(
    onMessage: (event: ServerSentEvent<T>) => void
  ) {
    this.parser = serverSideEventsStreamingResponseParser(
      onMessage
    ) as ResponseParser<T>;
    return this;
  }

  withFailureOnStatusCode(statusCodes: number[]) {
    this.parser = failureOnStatusCodeFactory(statusCodes) as ResponseParser<T>;
    return this;
  }

  withEmptyResponse() {
    this.parser = emptyResponseParser as ResponseParser<T>;
    return this;
  }

  build(): HttpRequest<T> {
    if (!this.parser) {
      throw new Error('Response parser is required');
    }

    if (!this.path) {
      throw new Error('Path is required');
    }

    const path = PathBuilder.aPath(this.path)
      .withQueryParams(this.queryParams)
      .build();

    const defaultAuthMiddleware = async (res: Response) => res;
    return {
      path,
      request: this.request,
      responseParser: this.parser,
      authMiddleware: defaultAuthMiddleware
    };
  }
}
